"use client";

import { GitCompare } from "lucide-react";
import { useEffect, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { authService } from "@/services/auth";

interface NarrativeResponse {
  narrative: string | null;
  servedBy: "local" | "cloud" | "none";
  truncated: boolean;
}

type State =
  | { status: "loading" }
  | { status: "done"; data: NarrativeResponse };

/**
 * 実行間 diff 変化ナラティブ（④）の表示。
 * - diff 一覧の描画は遅らせない。生成は描画後（アイドル時）に開始する。
 * - 生成中は「変化サマリを生成中…」インジケータを出し、出来たら差し替える。
 * - 初回実行（previousRunId=null）は何も出さない。
 * - ローカル LLM 不在 / narrative 無しなら何も描画しない（グレースフル劣化）。
 * - 「AI変化サマリ(参考)」ラベル固定。数値は下の新規/解消チップ側が権威。使い捨て。
 */
export function RunDiffNarrative({
  runId,
  previousRunId,
}: {
  runId: string | null | undefined;
  previousRunId: string | null;
}) {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    // 初回実行（比較対象なし）は生成そのものをしない。
    if (!runId || previousRunId == null) return;
    let cancelled = false;
    setState({ status: "loading" });

    const finishHidden = () => {
      if (!cancelled) {
        setState({
          status: "done",
          data: { narrative: null, servedBy: "none", truncated: false },
        });
      }
    };
    const start = () => {
      void (async () => {
        try {
          const token = await authService.getIdToken();
          if (cancelled) return;
          if (!token) {
            finishHidden();
            return;
          }
          const res = await fetch(
            apiUrl(
              `/api/aicheck/run/result/${encodeURIComponent(runId)}/diff-narrative`,
            ),
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (cancelled) return;
          if (!res.ok) {
            finishHidden();
            return;
          }
          const json = (await res.json()) as { data: NarrativeResponse };
          if (!cancelled) setState({ status: "done", data: json.data });
        } catch {
          // 付加機能。失敗は静かに無表示。
          finishHidden();
        }
      })();
    };

    const w = window as unknown as {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(start, { timeout: 2000 });
    } else {
      timerId = setTimeout(start, 500);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof w.cancelIdleCallback === "function") {
        w.cancelIdleCallback(idleId);
      }
      if (timerId !== undefined) clearTimeout(timerId);
    };
  }, [runId, previousRunId]);

  // 初回実行は比較対象が無いので何も出さない。
  if (!runId || previousRunId == null) return null;

  // 生成中インジケータ（diff 一覧はブロックしない。生成完了で差し替わる）
  if (state.status === "loading") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-violet-100 bg-violet-50/50 px-4 py-2.5 text-xs text-violet-400">
        <GitCompare size={14} className="animate-pulse" />
        変化サマリを生成中…
      </div>
    );
  }

  // narrative が出なければ何も出さない（ローカル LLM 不在時の劣化）
  if (!state.data.narrative) return null;

  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50/50 px-4 py-3">
      <div className="mb-1 flex items-center gap-1.5">
        <GitCompare size={14} className="text-violet-500" />
        <span className="text-[11px] font-semibold text-violet-600">
          AI変化サマリ（参考）
        </span>
        {state.data.truncated && (
          <span className="text-[10px] text-violet-400">
            （変化が多いため一部を要約）
          </span>
        )}
      </div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
        {state.data.narrative}
      </p>
    </div>
  );
}
